import { StudyNote, Category } from '../types';

export interface SaveData {
  notes: StudyNote[];
  categories: Category[];
  savedAt: string;
  mapPositions?: Record<string, { x: number; y: number }>;
}

// ─── IndexedDB helpers (used as fallback AND to persist the file handle) ───

const DB_NAME = 'bible-study-db';
const DB_VERSION = 2;
const KV_STORE = 'kv';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(KV_STORE)) {
        db.createObjectStore(KV_STORE);
      }
      // Remove old stores from v1 if present
      if (db.objectStoreNames.contains('notes')) db.deleteObjectStore('notes');
      if (db.objectStoreNames.contains('categories')) db.deleteObjectStore('categories');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KV_STORE, 'readonly');
    const req = tx.objectStore(KV_STORE).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KV_STORE, 'readwrite');
    tx.objectStore(KV_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── File System Access API ───

const FILE_HANDLE_KEY = 'fileHandle';
const fsaSupported = typeof window !== 'undefined' && 'showSaveFilePicker' in window;

let cachedHandle: FileSystemFileHandle | null = null;

async function getFileHandle(): Promise<FileSystemFileHandle | null> {
  if (cachedHandle) return cachedHandle;
  try {
    const handle = await idbGet<FileSystemFileHandle>(FILE_HANDLE_KEY);
    if (handle) {
      let perm = await (handle as any).queryPermission({ mode: 'readwrite' });
      if (perm === 'prompt') {
        // Re-request permission — this succeeds when called during a user gesture
        perm = await (handle as any).requestPermission({ mode: 'readwrite' });
      }
      if (perm === 'granted') {
        cachedHandle = handle;
        return cachedHandle;
      }
    }
  } catch { /* ignore */ }
  return null;
}

async function requestFileHandle(): Promise<FileSystemFileHandle | null> {
  if (!fsaSupported) return null;
  try {
    const handle = await (window as any).showSaveFilePicker({
      suggestedName: 'bible-study-notes.json',
      types: [{ description: 'JSON file', accept: { 'application/json': ['.json'] } }],
    });
    cachedHandle = handle;
    await idbSet(FILE_HANDLE_KEY, handle);
    return handle;
  } catch {
    return null;
  }
}

async function writeToFile(handle: FileSystemFileHandle, data: SaveData): Promise<void> {
  const writable = await (handle as any).createWritable();
  await writable.write(JSON.stringify(data, null, 2));
  await writable.close();
}

async function readFromFile(handle: FileSystemFileHandle): Promise<SaveData | null> {
  try {
    const file = await (handle as any).getFile();
    const text = await file.text();
    return JSON.parse(text) as SaveData;
  } catch {
    return null;
  }
}

// ─── Public API ───

let pendingNotes: StudyNote[] | null = null;
let pendingCategories: Category[] | null = null;

/** Returns true if a file handle has been stored (even if permission hasn't been re-granted yet this session). */
export const hasSaveFileStored = async (): Promise<boolean> => {
  try {
    const handle = await idbGet<FileSystemFileHandle>(FILE_HANDLE_KEY);
    return !!handle;
  } catch {
    return false;
  }
};

/** Call this once on startup — returns true if a save file was found and loaded. */
export const initStorage = async (): Promise<{ notes: StudyNote[]; categories: Category[]; hasSaveFile: boolean }> => {
  // Try File System Access first
  if (fsaSupported) {
    const handle = await getFileHandle();
    if (handle) {
      const data = await readFromFile(handle);
      if (data) {
        return { notes: data.notes ?? [], categories: data.categories ?? [], hasSaveFile: true };
      }
    }
  }

  // Fallback: IndexedDB
  const notes = await idbGet<StudyNote[]>('notes') ?? [];
  const categories = await idbGet<Category[]>('categories') ?? [];
  return { notes, categories, hasSaveFile: false };
};

/** Prompt the user to pick/create a save file. Returns true on success. */
export const pickSaveFile = async (notes: StudyNote[], categories: Category[]): Promise<boolean> => {
  const handle = await requestFileHandle();
  if (!handle) return false;
  await writeToFile(handle, { notes, categories, savedAt: new Date().toISOString() });
  return true;
};

const flush = async (notes: StudyNote[], categories: Category[]) => {
  // Always write to IDB as fallback
  await idbSet('notes', notes);
  await idbSet('categories', categories);

  // Also write to file if handle available
  const handle = await getFileHandle();
  if (handle) {
    try {
      await writeToFile(handle, { notes, categories, savedAt: new Date().toISOString() });
    } catch (e: any) {
      // Permission may have lapsed — will be retried next time user interacts
      console.warn('File write failed (permission may need renewal):', e?.message);
    }
  }
};

export type NodePositions = Record<string, { x: number; y: number }>;

export const saveMapPositions = async (positions: NodePositions): Promise<void> => {
  try {
    await idbSet('mapPositions', positions);
    const handle = await getFileHandle();
    if (handle) {
      const notes = pendingNotes ?? [];
      const categories = pendingCategories ?? [];
      try {
        await writeToFile(handle, { notes, categories, mapPositions: positions, savedAt: new Date().toISOString() });
      } catch (e: any) {
        console.warn('File write failed (permission may need renewal):', e?.message);
      }
    }
  } catch (error) {
    console.error('Error saving map positions:', error);
  }
};

export const loadMapPositions = async (): Promise<NodePositions> => {
  try {
    return await idbGet<NodePositions>('mapPositions') ?? {};
  } catch {
    return {};
  }
};

export const clearMapPositions = async (): Promise<void> => {
  try {
    await idbSet('mapPositions', {});
  } catch {
    // ignore
  }
};

export const saveNotes = async (notes: StudyNote[]): Promise<void> => {
  pendingNotes = notes;
  await flush(notes, pendingCategories ?? []);
};

export const saveCategories = async (categories: Category[]): Promise<void> => {
  pendingCategories = categories;
  await flush(pendingNotes ?? [], categories);
};

export const loadNotes = async (): Promise<StudyNote[]> => {
  const result = await initStorage();
  pendingNotes = result.notes;
  return result.notes;
};

export const loadCategories = async (): Promise<Category[]> => {
  const result = await initStorage();
  pendingCategories = result.categories;
  return result.categories;
};
